import { BridgeSession } from './runtime/BridgeSession.js';
import { readRuntimeConfig } from './runtime/RuntimeConfig.js';
import type { Difficulty } from './songpack/index.js';

/**
 * Browser entry point.
 *
 * Deliberately outside `view/`: it composes the runtime, and the view layer must
 * not depend on anything above it. The eventual host application will own song
 * selection and hand the runtime a canvas; this reads a query string so a
 * different pack or difficulty can be tried without a rebuild.
 *
 *   ?pack=/OS%20mismatch%20syndrome.zip&difficulty=hard&speed=1.5&mirrors=6&notes=off
 *
 * `?engine=` points CREATE SONG at the local Python chart engine bridge, and
 * `?engine=off` disables the feature outright.
 */

declare global {
  interface Window {
    /** Exposed so the automated browser check can read live state. */
    mandalizmBridge?: BridgeSession;
  }
}

const canvas = document.querySelector<HTMLCanvasElement>('#stage');
if (canvas === null) throw new Error('Canvas #stage is missing from the page.');

const params = new URLSearchParams(window.location.search);
const env = (import.meta as ImportMeta & { env?: { MODE?: string; VITE_RUNTIME_MODE?: string; DEV?: boolean } }).env;
const runtime = readRuntimeConfig(window.location.search, env?.VITE_RUNTIME_MODE ?? env?.MODE);
// Only used to seed an empty library. Songs come from the library after that;
// `?pack=none` starts with nothing at all.
const packParam = params.get('pack');
const songPackUrl = !runtime.allowExternalMedia || packParam === 'none' ? null : packParam;
const requested = params.get('difficulty');
const difficulty: Difficulty =
  requested === 'easy' || requested === 'hard' ? requested : 'normal';
const mirrors = Number.parseInt(params.get('mirrors') ?? '', 10);
// `?notes=off` silences the note word. It is on by default now: one of the six
// words swelling into the best moment is part of how PERFECT is read.
const previewNoteWords = params.get('notes') !== 'off';
const waveSpeed = Number.parseFloat(params.get('speed') ?? '');
// CREATE SONG needs a local Python process, so it is worth being able to turn
// off — and worth being able to point elsewhere when the bridge runs on another
// port. The default matches the bridge's own default.
const engine = params.get('engine');
const chartEngine =
  !runtime.allowChartEngine || engine === 'off' ? null : engine === null ? {} : { baseUrl: engine };

// `?debug=1` brings back the developer view: renderer frame timings, draw
// counts, live wave count, the pack's storage id and the music-video shard
// note. All of it is useful when something looks wrong and none of it means
// anything to somebody who came to play, so it is off unless asked for.
const debug = params.get('debug') === '1' || params.get('debug') === 'true';

const session = new BridgeSession({
  canvas,
  runtimeMode: runtime.runtimeMode,
  demoRequested: runtime.demoRequested,
  demoTrackId: params.get('demoTrack') ?? 'demo01',
  songPackUrl,
  difficulty,
  previewNoteWords,
  chartEngine,
  debug,
  developmentDiagnostics: (env?.DEV === true && debug) || params.get('audioDebug') === '1',
  ...(Number.isFinite(waveSpeed) ? { waveSpeed } : {}),
  ...(Number.isFinite(mirrors) ? { mirrorCount: mirrors } : {}),
});
window.mandalizmBridge = session;
void session.load();

/**
 * Tear the session down when Vite replaces this module.
 *
 * Without this, every edit during development builds a *second* session on top
 * of the first. The old one is never told to stop, so it keeps everything it
 * owns: its full-viewport overlays stay in the document, its `pointerdown`
 * listener stays on the canvas, its render loop keeps drawing to that same
 * canvas, and its six video decoders keep running.
 *
 * After a few edits the page therefore has several stacked overlays — clicks
 * land on a dead one whose handlers point at a disposed session — and two or
 * more render loops fighting over one canvas, so the music video and the wave
 * layer flicker or vanish depending on which loop drew last. The symptoms read
 * as "the UI stopped responding and nothing renders", which is nothing like the
 * cause, and they only appear in a tab that has been open across an edit.
 *
 * `import.meta.hot` is reached through a cast rather than by adding
 * `vite/client` to the type surface: this project declares `types: []` on
 * purpose, and one dev-server hook is not worth widening that.
 */
const hot = (import.meta as ImportMeta & {
  hot?: { dispose(callback: () => void): void };
}).hot;
if (hot !== undefined) {
  hot.dispose(() => {
    // Deleted rather than set to undefined: `exactOptionalPropertyTypes` draws a
    // distinction between "absent" and "present but undefined", and absent is
    // what an optional global should be once its owner is gone.
    delete window.mandalizmBridge;
    session.dispose();
  });
}
